import PropTypes from 'prop-types'
import {
  createContext,
  memo,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'

// The tooltip context
const TooltipContext = createContext({})

// Hook for getting the tooltip context
export const useTooltipContext = () => {
  const context = useContext(TooltipContext)

  return context
}

/**
 * The `TooltipProvider`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the react element
 */
function TooltipProvider (props) {
  const { children } = props
  const [tooltipRef, setTooltipRef] = useState({})
  const [tooltipId, setTooltipId] = useState('tooltip')
  const [tooltipTarget, setTooltipTarget] = useState('standard')
  const [tooltipLocationStart, setTooltipLocationStart] = useState({
    x: 0,
    y: 0
  })
  const [isTooltipActive, setIsTooltipActive] = useState(false)

  /**
   * Show the tooltip
   */
  const showTooltip = useCallback(({ clientX, clientY, target }, uid) => {
    setTooltipId(uid)
    setTooltipTarget(target)
    setTooltipLocationStart({ x: clientX, y: clientY })
    setIsTooltipActive(true)
  }, [])

  /**
   * Hide the tooltip
   */
  const hideTooltip = useCallback(() => {
    setIsTooltipActive(false)
  }, [])

  /**
   * Memo: The context
   */
  const ctx = useMemo(
    () => ({
      hideTooltip,
      isTooltipActive,
      setTooltipRef,
      showTooltip,
      tooltipId,
      tooltipLocationStart,
      tooltipTarget,
      tooltipRef
    }),
    [
      hideTooltip,
      isTooltipActive,
      setTooltipRef,
      showTooltip,
      tooltipId,
      tooltipLocationStart,
      tooltipTarget,
      tooltipRef
    ]
  )

  return (
    <TooltipContext.Provider value={ctx}>{children}</TooltipContext.Provider>
  )
}

/** @type {object} */
TooltipProvider.propTypes = {
  children: PropTypes.node
}

/** @type {object} */
TooltipProvider.defaultProps = {
  children: []
}

// Memoize
export default memo(TooltipProvider)
